import {
  ButtonHTMLAttributes,
  CSSProperties,
  LegacyRef,
  ReactNode,
} from "react";
import { Icon } from "../Icon";
import { LoadingDots } from "../LoadingDots";
import "./../../globals.scss";
import styles from "./button.module.scss";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  innerRef?: LegacyRef<HTMLButtonElement> | null;
  tooltip?: string;
  autoFocus?: boolean;
  className?: string;
  ai?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (e?: any) => void;
  icon?: string;
  iconPosition?: "left" | "right";
  large?: boolean; // legacy
  size?: "sm" | "md" | "lg";
  circular?: boolean;
  disabled?: boolean;
  selected?: boolean;
  secondary?: boolean;
  /** Text only, no background or border. Combine with a colour prop (e.g. `blue`, `success`) for color. */
  text?: boolean;
  success?: boolean;
  danger?: boolean;
  warning?: boolean;
  dark?: boolean;
  blue?: boolean;
  purple?: boolean;
  type?: "button" | "submit" | "reset";
  isLoading?: boolean;
  children?: ReactNode;
  wrapperStyles?: {
    outer?: CSSProperties;
    inner?: CSSProperties;
    loader?: CSSProperties;
  };
  lowerOpacity?: boolean;
}

export function Button({
  innerRef,
  tooltip,
  autoFocus,
  className,
  ai,
  onClick,
  icon,
  iconPosition = "left",
  large,
  size = "md",
  circular,
  disabled,
  selected,
  secondary,
  text,
  success,
  danger,
  warning,
  dark,
  blue,
  purple,
  isLoading,
  type = "button",
  lowerOpacity,
  children,
  wrapperStyles,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      ref={innerRef ? innerRef : null}
      autoFocus={autoFocus ? true : false}
      disabled={disabled}
      type={type}
      className={`
        ${className ? className : ""} 
        ${styles.button} 
        ${dark ? styles.dark : ""}
        ${blue ? styles.blue : ""}
        ${purple ? styles.purple : ""}
        ${secondary ? styles.secondary : ""} 
        ${text ? styles.text : ""}
        ${success ? styles.success : ""} 
        ${danger ? styles.danger : ""} 
        ${warning ? styles.warning : ""} 
        ${large ? styles.large : ""} 
        ${size ? styles[`size-${size}`] : ""}
        ${circular ? styles.circular : ""}
        ${selected ? styles.selected : ""}
        ${disabled ? styles.disabled : ""}
        ${ai ? styles.aiButton : ""}
        ${lowerOpacity ? styles.lowerOpacity : ""}
        `}
      onClick={onClick}
      title={tooltip || rest.title}
    >
      <span
        className={styles.relativeContentWrapper}
        style={wrapperStyles?.outer}
      >
        <span
          className={styles.btnContent}
          style={{
            ...wrapperStyles?.inner,
            ...(isLoading ? { visibility: "hidden" } : {}),
          }}
        >
          {icon && iconPosition === "left" && (
            <Icon name={icon} style={{ paddingRight: circular ? 0 : 6 }} />
          )}
          {children}
          {icon && iconPosition === "right" && (
            <Icon name={icon} style={{ paddingLeft: circular ? 0 : 6 }} />
          )}
        </span>
        {isLoading && (
          <div
            aria-hidden
            className={styles.loaderContainer}
            style={wrapperStyles?.loader}
          >
            <LoadingDots />
          </div>
        )}
      </span>
    </button>
  );
}
